import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListSprints } from '@data/hooks/sprints';
import { useOrganizationId } from '@app/utils/hooks';
import { Skeleton } from '@ui/components/ui/skeleton';

import WorkforceGrid from '../products/productDetail/WorkforceGrid';
import ProductPicker from './ProductPicker';
import { MAX_PRICED, useProductRevenue } from './productRevenue';
import { sprintSeries, useProductHoursBySprint, yearSprintWindows } from './productTime';

/** Nothing is typed in: a figure is changed on that product's own Team and rates tab. */
export const RevenuePerProductCard = (): ReactElement => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId() ?? '';

  const { data: sprintsData, isLoading: sprintsLoading, isError: sprintsFailed } = useListSprints({ limit: 200 });
  const sprints = useMemo(() => sprintsData?.items ?? [], [sprintsData]);
  // The year's sprints that have started, which is the same span the chart above plots.
  const windows = useMemo(() => yearSprintWindows(sprints, new Date()), [sprints]);
  const { bySprint, isLoading: hoursLoading, isError: hoursFailed } = useProductHoursBySprint(organizationId, windows);
  const allSeries = useMemo(() => sprintSeries(bySprint), [bySprint]);

  // `null` = nobody has picked yet, so the table follows the figures: the busiest products of the year.
  const [picked, setPicked] = useState<string[] | null>(null);
  const selected = picked ?? allSeries.slice(0, MAX_PRICED).map(series => series.key);
  const chosen = useMemo(
    () => allSeries.filter(series => selected.includes(series.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSeries, selected.join()],
  );

  // The same objects the reports are asked for, so columns and figures cannot fall out of step.
  const columns = useMemo(
    () => windows.map(window => sprints.find(sprint => sprint.id === window.id)).filter(sprint => !!sprint),
    [windows, sprints],
  );
  const {
    revenue,
    isLoading: revenueLoading,
    isError: revenueFailed,
  } = useProductRevenue(
    chosen.map(series => ({ id: series.key, name: series.label })),
    columns,
  );

  if (sprintsLoading || hoursLoading) return <Skeleton className="h-64 w-full rounded-2xl" />;

  // Never a confident €0 on a failed read: an empty grid here reads as a year that earned nothing.
  if (sprintsFailed || hoursFailed || revenueFailed) {
    return (
      <p className="flex h-52 items-center justify-center rounded-2xl border border-dashed border-border px-4 text-center text-sm text-muted-foreground">
        {t('cardReadFailed')}
      </p>
    );
  }

  const byProduct: Record<string, Record<string, number>> = {};
  revenue.forEach(product => {
    Object.entries(product.bySprint).forEach(([sprintId, amount]) => {
      byProduct[sprintId] = { ...byProduct[sprintId], [product.id]: amount };
    });
  });

  return (
    <WorkforceGrid
      gridId={organizationId}
      title={t('insights.revenue.title')}
      hint={revenueLoading ? t('insights.revenue.pricing') : t('insights.revenue.hint')}
      nameLabel={t('titles.product')}
      emptyNote={t(allSeries.length ? 'insights.revenue.noneChosen' : 'insights.revenue.none')}
      // One block of rows: every product, in the order the year ranks them.
      groups={[{ key: '', label: '', people: revenue.map(product => ({ id: product.id, name: product.name })) }]}
      sprints={columns.map((sprint, index) => ({
        id: sprint.id,
        number: windows[index]?.number ?? 0,
        startDate: windows[index]?.from,
      }))}
      // Euros are what is stored: there are no hours behind these to price again.
      hours={byProduct}
      lead="euros"
      controls={
        allSeries.length > 0 && (
          <ProductPicker options={allSeries} value={selected} onChange={setPicked} max={MAX_PRICED} />
        )
      }
    />
  );
};

export default RevenuePerProductCard;
